import { useEffect, lazy, Suspense, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { PulsingOrbLoader } from '@/components/ui/pulsing-orb-loader';
import { Button } from '@/components/ui/button';
import { NavigationBar } from '@/components/landing/NavigationBar';
import { ScrollProgressBar } from '@/components/landing/ScrollProgressBar';
import { HeroSection } from '@/components/landing/HeroSection';
import { SEOHead } from '@/components/seo/SEOHead';
import { AuthEscapeActions } from '@/components/auth/AuthEscapeActions';

const PromiseStripSection = lazy(() => import('@/components/landing/PromiseStripSection').then(m => ({ default: m.PromiseStripSection })));
const UniversityLogoStrip = lazy(() => import('@/components/landing/UniversityLogoStrip').then(m => ({ default: m.UniversityLogoStrip })));
const WhyVerithosSection = lazy(() => import('@/components/landing/WhyVerithosSection').then(m => ({ default: m.WhyVerithosSection })));
const WorkspacePeekSection = lazy(() => import('@/components/landing/WorkspacePeekSection').then(m => ({ default: m.WorkspacePeekSection })));

// Lazy-load below-the-fold sections
const HowItWorksSection = lazy(() => import('@/components/landing/HowItWorksSection').then(m => ({ default: m.HowItWorksSection })));

const TestimonialsSection = lazy(() => import('@/components/landing/TestimonialsSection').then(m => ({ default: m.TestimonialsSection })));
const CTASection = lazy(() => import('@/components/landing/CTASection').then(m => ({ default: m.CTASection })));
const FooterSection = lazy(() => import('@/components/landing/FooterSection').then(m => ({ default: m.FooterSection })));

export default function Index() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showSlowHint, setShowSlowHint] = useState(false);

  useEffect(() => {
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!loading) {
      window.scrollTo(0, 0);
    }
  }, [loading]);

  useEffect(() => {
    if (loading || !user) return;

    // Show "taking longer than usual" hint after 3s
    const hintTimer = setTimeout(() => setShowSlowHint(true), 3000);

    const countPromise = supabase
      .from('projects')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', user.id);

    const timeoutPromise = new Promise<{ count: number | null }>((resolve) =>
      setTimeout(() => resolve({ count: null }), 5000)
    );

    Promise.race([countPromise, timeoutPromise])
      .then((res: { count: number | null }) => {
        const count = res?.count;
        const target = count === 0 ? '/onboarding/start' : '/projects';
        navigate(target, { replace: true });
      })
      .catch(() => navigate('/projects', { replace: true }))
      .finally(() => clearTimeout(hintTimer));

    return () => clearTimeout(hintTimer);
  }, [user, loading, navigate]);

  useEffect(() => {
    const state = location.state as { scrollTo?: string } | null;
    if (state?.scrollTo) {
      setTimeout(() => {
        document.getElementById(state.scrollTo!)?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  if (loading || user) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-4">
        <PulsingOrbLoader />
        {showSlowHint && (
          <div className="flex flex-col items-center gap-3 text-center">
            <p className="text-sm text-muted-foreground">Taking longer than usual…</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/projects', { replace: true })}
            >
              Continue to your projects
            </Button>
            <AuthEscapeActions hideBack className="mt-2" />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background relative">
      <div 
        aria-hidden="true"
        className="fixed inset-0 pointer-events-none z-0"
        style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, hsl(var(--foreground) / 0.055) 0.5px, transparent 0)',
          backgroundSize: '22px 22px',
        }}
      />
      <SEOHead />
      <ScrollProgressBar />
      <NavigationBar />
      <main>
        <HeroSection />
        
        <PromiseStripSection />
        <UniversityLogoStrip />
        <WhyVerithosSection />
        <WorkspacePeekSection />
        <Suspense fallback={null}>
          <HowItWorksSection />
          <TestimonialsSection />
          <CTASection />
          <FooterSection />
        </Suspense>
      </main>
    </div>
  );
}
